import React, { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { fmtCurrency } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Wallet, HandCoins } from "lucide-react";
import { toast } from "sonner";

export const RefundDialog = ({ open, onOpenChange, pay, cur, onDone }) => {
  const max = pay.refundable ?? 0;
  const [amount, setAmount] = useState(String(max));
  const [method, setMethod] = useState(pay.card_refundable ? "card" : "manual");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount(String(max)); setMethod(pay.card_refundable ? "card" : "manual"); setReason(""); } }, [open, max, pay.card_refundable]);
  const amt = parseFloat(amount) || 0;
  const bad = amt <= 0 || amt > max + 0.005;
  const methods = [
    { id: "card", label: "Back to card", sub: pay.card_refundable ? "Stripe refund to the card used" : "Not a card payment we can refund", icon: CreditCard, off: !pay.card_refundable },
    { id: "credit", label: "Account credit", sub: pay.client_id ? "Add to the client's credit balance" : "No client file on this booking", icon: Wallet, off: !pay.client_id },
    { id: "manual", label: "Refunded in cash / other", sub: "Hand the money back and record it here", icon: HandCoins },
  ];
  const go = async () => {
    setBusy(true);
    try { await api.post(pay.booking_id ? `/bookings/${pay.booking_id}/refund` : `/sales/${pay.sale_id}/refund`, { amount: amt, method, reason }); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="refund-dialog">
        <DialogHeader><DialogTitle className="font-heading">Refund {pay.client_name || "this sale"}</DialogTitle><DialogDescription>Up to {fmtCurrency(max, cur, 2)} can be refunded from invoice {pay.invoice_no}.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Amount</label>
            <div className="flex gap-2">
              <Input type="number" inputMode="decimal" step="0.01" min="0.01" max={max} value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-lg" data-testid="refund-amount" />
              <Button type="button" variant="outline" className="rounded-lg h-11" onClick={() => setAmount(String(max))} data-testid="refund-full">Full</Button>
            </div>
          </div>
          <div className="space-y-1.5">
            {methods.map((m) => (
              <button key={m.id} type="button" disabled={m.off} onClick={() => setMethod(m.id)} data-testid={`refund-method-${m.id}`}
                className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${method === m.id ? "border-primary bg-primary/5" : "border-border"} ${m.off ? "opacity-50 cursor-not-allowed" : "hover:border-primary/60"}`}>
                <m.icon className="h-4 w-4 text-primary shrink-0" />
                <span className="min-w-0"><span className="block text-sm font-medium">{m.label}</span><span className="block text-[11px] text-muted-foreground truncate">{m.sub}</span></span>
              </button>
            ))}
          </div>
          <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-11 rounded-lg" data-testid="refund-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-lg" onClick={() => onOpenChange(false)} data-testid="refund-cancel">Cancel</Button>
          <Button className="rounded-lg" disabled={bad || busy} onClick={go} data-testid="refund-submit">{busy ? "Refunding…" : `Refund ${fmtCurrency(amt, cur, 2)}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
